/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
// Extends From
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal Imports
import AdministeredBy from 'linced-app/enums/medical/administered-by';
import AdministeredByAdminister from 'linced-app/enums/medical/log-administered';
import Block from 'linced-app/enums/medical/block';
import LogType from 'linced-app/enums/medical/log-type';
import MedicationType from 'linced-app/enums/medical/medication-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';
import { hash } from '@linc-technologies/linced-common/utils/promise';
// External Imports
import moment from 'moment';

const autoinjectorAndInhalerTypes = [
  MedicationType.AUTOINJECTOR___STANDARD,
  MedicationType.AUTOINJECTOR___JUNIOR,
  MedicationType.ASTHMA_INHALER___GENERAL_USE,
];

export default class MedicationAdminister extends Component {
  @service
  session;

  @service
  store;

  AdministeredBy = AdministeredBy;
  AdministeredByAdminister = AdministeredByAdminister;
  Block = Block;
  @tracked
  administeredByPeople = null;
  @tracked
  administeredToPeople = null;
  @tracked
  dialogOpen = false;
  @tracked
  log = null;
  @tracked
  medication = null;
  medications = null;
  @tracked schedule = null;
  @tracked
  scheduleTimeWarning = false;
  openDecisionDialog = false;
  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get isNSW() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  // We only want medications for this student, or for the whole school
  // and only those that actually have quantities
  @computed('medications.[]', 'person')
  get filteredMedications() {
    return this.medications.filter((medication) => {
      return (
        (isEmpty(medication.person_id) ||
          medication.person_id === this.person.get('id')) &&
        medication.quantity > 0
      );
    });
  }

  @computed('medication.nonDeletedSchedules.[]', 'schedule')
  get scheduledMedicationWarning() {
    return !this.schedule && this.get('medication.nonDeletedSchedules.length');
  }

  @computed('isNSW', 'log.medication.type')
  get showReplaced() {
    return (
      this.isNSW &&
      autoinjectorAndInhalerTypes.includes(this.log.medication.get('type'))
    );
  }

  @computed('medication.person.{preferredGender,type}')
  get pronoun() {
    if (this.medication.person.get('type') === PersonType.STAFF) {
      return 'their';
    }
    switch (this.medication.person.get('preferredGender')) {
      case 'M':
        return 'his';
      case 'F':
        return 'her';
    }
    return 'their';
  }

  @action
  async openDialog() {
    // A belongsTo takes a record, not the async proxy. Callers can pass
    // schedule.medication or sickbayVisit.learner.
    let args = await hash({ medication: this.medication, person: this.person });
    if (this.isDestroying || this.isDestroyed) {
      return;
    }
    this.medication = args.medication;
    this.log = this.store.createRecord('medical/log', {
      schoolId: this.get('session.school'),
      logType: LogType.ADMINISTRATION,
    });
    if (this.medication) {
      this.log.set('medication', this.medication);
      // A belongsTo takes a record, not the async proxy.
      let person = await this.medication.person;
      if (person) {
        this.log.setProperties({
          administeredTo: person.type,
          administeredToPerson: person,
        });
      }
      if (this.medication.get('nonEmptyBatches').length === 1) {
        this.log.set('batch', this.medication.get('nonEmptyBatches')[0]);
      }
      if (
        this.medication.get('administeredBy') ===
        AdministeredBy.ADMINISTERED_BY_AUTHORISED_STAFF
      ) {
        this.log.set('administeredBy', AdministeredByAdminister.STAFF);
        this.administeredByPeople = this.medication.get('authorisedStaff');
      }
    }
    if (args.person) {
      this.log.setProperties({
        administeredTo: args.person.type, // THIS ACTUALLY WORKS
        administeredToPerson: args.person,
      });
    }
    if (this.schedule) {
      // Administration time is out from schedule by more than 30 minutes
      let scheduledTime = moment(this.schedule.get('time'), 'HH:mm').unix();
      this.scheduleTimeWarning =
        Math.abs(moment().unix() - scheduledTime) > 1800;
      // Auto set dosage
      this.log.set('dosage', this.schedule.get('dosage'));
    }
    this.log.set('displayDate', moment().unix());
    this.dialogOpen = true;
  }

  @action
  closeDialog() {
    this.log.deleteRecord();
    this.log = null;
    if (this.mode === 'sickbay') {
      this.medication = null;
    }
    this.dialogOpen = false;
  }

  @action
  onSave() {
    this.dialogOpen = false;
  }

  @action
  setMedication(medication) {
    this.medication = medication;
    this.administeredByPeople = null;
    this.log.setProperties({
      administeredBy: null,
      medication: this.medication,
      batch: null,
    });
    if (this.medication.get('nonEmptyBatches').length === 1) {
      this.log.set('batch', this.medication.get('nonEmptyBatches')[0]);
    }
    if (
      this.medication.get('administeredBy') ===
      AdministeredBy.ADMINISTERED_BY_AUTHORISED_STAFF
    ) {
      this.log.set('administeredBy', AdministeredByAdminister.STAFF);
      this.administeredByPeople = this.medication.get('authorisedStaff');
    }
  }

  @action
  setDate(date) {
    this.log.set(
      'displayDate',
      moment(
        date + ' ' + moment.unix(this.log.displayDate).format('HH:mm'),
        'YYYY-MM-DD HH:mm',
      ).unix(),
    );
  }

  @action
  setTime(time) {
    this.log.set(
      'displayDate',
      moment(
        moment.unix(this.log.displayDate).format('YYYY-MM-DD') + ' ' + time,
        'YYYY-MM-DD HH:mm',
      ).unix(),
    );
  }

  @action
  setBlock(block) {
    this.log.set('block', block.value);
  }

  @action
  setAdministeredBy(administeredBy) {
    let value = administeredBy.value;
    this.log.setProperties({
      administeredBy: value,
      administeredByPerson: null,
      administeredByString: '',
    });
    if (value === AdministeredByAdminister.STUDENT) {
      this.administeredByPeople = this.store
        .peekAll('person')
        .filter((person) => person.type === PersonType.LEARNER);
    }
    if (value === AdministeredByAdminister.STAFF) {
      this.administeredByPeople = this.store
        .peekAll('person')
        .filter((person) => person.type === PersonType.STAFF);
    }
  }

  @action
  setAdministeredTo(administeredTo) {
    let value = administeredTo.value;
    this.log.setProperties({
      administeredTo: value,
      administeredToPerson: null,
      administeredToString: '',
    });
    if (value === AdministeredByAdminister.STUDENT) {
      this.administeredToPeople = this.store
        .peekAll('person')
        .filter((person) => person.type === PersonType.LEARNER);
    }
    if (value === AdministeredByAdminister.STAFF) {
      this.administeredToPeople = this.store
        .peekAll('person')
        .filter((person) => person.type === PersonType.STAFF);
    }
  }

  @action
  removeAdministeredByPerson() {
    this.log.set('administeredByPerson', null);
  }

  @action
  async removeAdministeredToPerson() {
    this.log.set('administeredToPerson', null);
    if (this.medication.person_id && this.medication.get('allowSiblingUse')) {
      // A belongsTo takes a record, not the async proxy.
      let person = await this.medication.person;
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
      let administeredToPeople = mapBy(person.currentSiblings, 'person');
      administeredToPeople.push(person);
      this.administeredToPeople = administeredToPeople;
    }
  }
}
